import { Stack, Box, Divider } from "@mui/material";
import { useTBContext } from "../Contexts/useTBStates";
import { SepiaChip } from "../../shared/Components/SepiaChip";
import type { EventGroupType } from "@moment-golf/types";

interface ChipItem {
    label: string;
    active: boolean;
    onClick: () => void;
    disabled?: boolean;
}

export const TabChipSelectorsBar = () => {
    const {
        appSection,
        viewPanel, setViewPanel,
        viewEvent,
        eventTabValue, setEventTabValue,
        viewGroup,
        groupTabValue, setGroupTabValue,
        groupTypeFilter, setGroupTypeFilter,
        editMode,
        factor,
    } = useTBContext();

    const scale = factor < 1 ? factor : 1;

    let chips: ChipItem[] = [];

    if (appSection === 'tournaments') {
        if (viewEvent) {
            // Event editor tabs — only shown when an event is open
            const eventTabs = [
                { label: 'Event Details', idx: 0 },
                { label: 'Flighting', idx: 1 },
                { label: 'Comms & Info', idx: 2 },
                { label: 'Registrations', idx: 3 },
                { label: 'Round Details', idx: 4 },
                { label: 'Games', idx: 5 },
                { label: 'Tee Sheet', idx: 6 },
            ];
            chips = eventTabs.map(({ label, idx }) => ({
                label,
                active: eventTabValue === idx,
                onClick: () => { if (!editMode) setEventTabValue(idx); },
                disabled: editMode && eventTabValue !== idx,
            }));
        }
        // Overview (snapshot) and Tournaments list show no chips
    } else if (appSection === 'groups') {
        if (viewEvent) {
            // Event editor nested under a collection — same tabs as tournaments
            const eventTabs = [
                { label: 'Event Details', idx: 0 },
                { label: 'Flighting', idx: 1 },
                { label: 'Comms & Info', idx: 2 },
                { label: 'Registrations', idx: 3 },
                { label: 'Round Details', idx: 4 },
                { label: 'Games', idx: 5 },
                { label: 'Tee Sheet', idx: 6 },
            ];
            chips = eventTabs.map(({ label, idx }) => ({
                label,
                active: eventTabValue === idx,
                onClick: () => { if (!editMode) setEventTabValue(idx); },
                disabled: editMode && eventTabValue !== idx,
            }));
        } else if (viewGroup) {
            // Collection editor tabs
            const groupTabs = [
                { label: 'Events', idx: 0 },
                { label: 'Players', idx: 1 },
                { label: 'Series Points', idx: 2 },
                { label: 'Standings', idx: 3 },
                { label: 'Collection Details', idx: 4 },
            ];
            chips = groupTabs.map(({ label, idx }) => ({
                label,
                active: groupTabValue === idx,
                onClick: () => { if (!editMode) setGroupTabValue(idx); },
                disabled: editMode && groupTabValue !== idx,
            }));
        } else {
            // Group type filter chips
            const typeChips: { label: string; filter: EventGroupType | 'all' }[] = [
                { label: 'Series / Tours', filter: 'series' },
                { label: 'Leagues', filter: 'league' },
                { label: 'Trips', filter: 'trip' },
/*                 { label: 'All', filter: 'all' }, */
            ];
            chips = typeChips.map(({ label, filter }) => ({
                label,
                active: groupTypeFilter === filter,
                onClick: () => setGroupTypeFilter(filter),
            }));
        }
    } else if (appSection === 'userManagement') {
        chips = [
            {
                label: 'User Profile',
                active: viewPanel === 'userProfile',
                onClick: () => setViewPanel('userProfile'),
            },
            {
                label: 'Rewards',
                active: viewPanel === 'rewards',
                onClick: () => setViewPanel('rewards'),
            },
            {
                label: 'Credits & Discounts',
                active: viewPanel === 'creditsDiscounts',
                onClick: () => setViewPanel('creditsDiscounts'),
            },
        ];
    } else if (appSection === 'commandCenter') {
        // Scoring chips (A–Z), then Operations chips (A–Z)
        chips = [
            { label: 'Attest Scores', active: viewPanel === 'attestScores', onClick: () => setViewPanel('attestScores') },
            { label: 'Challenges', active: viewPanel === 'challenges', onClick: () => setViewPanel('challenges') },
            { label: 'Leaderboard', active: viewPanel === 'leaderboard', onClick: () => setViewPanel('leaderboard') },
            { label: 'Scorecard Playoff', active: viewPanel === 'playoff', onClick: () => setViewPanel('playoff') },
            { label: 'First Tee', active: viewPanel === 'firstTee', onClick: () => setViewPanel('firstTee') },
            { label: 'Pace of Play', active: viewPanel === 'paceOfPlay', onClick: () => setViewPanel('paceOfPlay') },
            { label: 'Player Management', active: viewPanel === 'playerManagement', onClick: () => setViewPanel('playerManagement') },
        ];
    }

    const scoringChipCount = 4;
    const showCcDivider = appSection === 'commandCenter' && chips.length > scoringChipCount;

    return (
        <Box
            id='tournament-builder-chip-bar'
            sx={{
                height: 40 * scale,
                minHeight: 40 * scale,
                backgroundColor: 'sepia.background',
                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.1)',
                px: 2,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
            }}
        >
            <Stack direction='row' spacing={1} alignItems='center'>
                {chips.map((chip, index) => (
                    <Stack key={chip.label} direction='row' spacing={1} alignItems='center'>
                        {showCcDivider && index === scoringChipCount && (
                            <Divider
                                orientation='vertical'
                                flexItem
                                sx={{ borderColor: 'sepia.extraLight', mx: 0.5 }}
                            />
                        )}
                        <SepiaChip {...chip} />
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
};
